import {
  messageHasOwnBuild,
  messageHasPrototyperArtifact,
  resolvePrototypeName,
} from '@ideoz/core';
import type { Message, PrototypeVersion } from '@ideoz/core';
import {
  unfinishedBuildAction,
  versionHasNumber,
  versionSetAside,
  type TurnVersionsLookup,
  type UnfinishedBuildAction,
} from './turnVersions';

/**
 * The panel title's menu: the conversation's prototypes and their versions, so the founder can
 * open any of them without scrolling the chat to its card. Built from what the pills read (the
 * turn messages and the versions), so the menu and the pills always agree.
 */

export type VersionMenuEntryState = 'finished' | 'unfinished' | 'building';

export interface VersionMenuEntry {
  llmInteractionId: string;
  /** Passed to the same open handler a pill click uses. */
  demoLinkId?: string | number;
  /** "V4", "Didn't finish", "Building…". */
  label: string;
  state: VersionMenuEntryState;
  createdAt?: string;
  isCurrent: boolean;
  isPublished: boolean;
  /** The version's id on the server; absent while the versions are not loaded. */
  versionId?: string;
  /**
   * Continue is offered on this row: the newest unfinished build of its prototype. An older one
   * cannot be continued (only a prototype's newest version can be resumed), so it has no button.
   */
  canContinue: boolean;
  /** What that button does: finish the build, or build it again from the last finished version. */
  action: UnfinishedBuildAction;
  /** Remove is offered on this row: an unfinished build the server knows. */
  canRemove: boolean;
  /**
   * The scope a version of a startup's shared prototype was made in, for example "Clinic
   * Operations" (User Story 569). Absent for a prototype of this scope only.
   */
  madeIn?: string;
}

/**
 * One of the startup's shared prototypes (User Story 569, `STARTUP_PROTOTYPES`) with every
 * version, made in any scope. The menu lists the finished versions this conversation did not
 * make, so a scope can open what another scope built.
 */
export interface SharedPrototypeHistory {
  id: string;
  title: string | null;
  versions: readonly PrototypeVersion[];
}

export interface VersionMenuGroup {
  key: string;
  name: string;
  /** Newest first. */
  entries: VersionMenuEntry[];
}

/** Key prefix of a group made from a name, for turns whose version names no prototype. */
const NAME_KEY = 'name:';

export function buildPrototypeVersionMenu(input: {
  messages: readonly Message[];
  versions?: TurnVersionsLookup;
  /** The version on screen. */
  activeTurnId?: string | null;
  /** The turn building right now. */
  liveTurnId?: string | null;
  /** Version ids with a published link. */
  publishedVersionIds?: ReadonlySet<string>;
  /** The startup's shared prototypes, when the startup shares them across its scopes. */
  shared?: readonly SharedPrototypeHistory[];
}): VersionMenuGroup[] {
  const { messages, versions, activeTurnId, liveTurnId, publishedVersionIds, shared } = input;
  const ready = versions?.status === 'ready';
  const groups = new Map<string, VersionMenuGroup>();
  /** Each listed turn's place in the conversation, to order rows moved between groups. */
  const order = new Map<string, number>();
  let sawUser = false;

  for (const m of messages) {
    if (m.role === 'user') {
      sawUser = true;
      continue;
    }
    // As the pills: a turn that built something, or a prototyper turn with a user turn before it
    // (a scope's greeting is not one, and carries no build of its own).
    if (!m.llmInteractionId || !messageHasPrototyperArtifact(m)) continue;
    if (!sawUser && !messageHasOwnBuild(m)) continue;
    const lid = m.llmInteractionId;
    const version = ready ? versions!.get(lid) : undefined;
    if (versionSetAside(version?.status)) continue;

    const name =
      resolvePrototypeName({
        prototypeTitle: m.prototypeTitle,
        prototypeDisplayName: m.prototypeDisplayName,
        prototypeKind: m.prototypeKind,
      }) ?? 'Prototype';
    const key = version?.prototypeId ?? `${NAME_KEY}${name}`;

    const number = version?.number ?? (ready ? undefined : (m.prototypeVersionIndex ?? undefined));
    const settled = ready ? versionHasNumber(version) : m.buildStatus === 'success' || number != null;
    const state: VersionMenuEntryState =
      lid === liveTurnId ? 'building' : settled === false ? 'unfinished' : 'finished';
    const label =
      state === 'building'
        ? 'Building…'
        : state === 'unfinished'
          ? "Didn't finish"
          : typeof number === 'number' && number > 0
            ? `V${number}`
            : 'Version';

    let group = groups.get(key);
    if (!group) {
      group = { key, name, entries: [] };
      groups.set(key, group);
    }
    group.name = name;
    group.entries.unshift({
      llmInteractionId: lid,
      demoLinkId: m.prototyper_demo_link_id ?? undefined,
      label,
      state,
      createdAt: version?.createdAt ?? m.createdAt,
      isCurrent: lid === activeTurnId,
      isPublished: Boolean(version && publishedVersionIds?.has(version.id)),
      versionId: version?.id,
      canContinue: false,
      action: unfinishedBuildAction(version?.status),
      canRemove: state === 'unfinished' && version != null,
    });
    order.set(lid, order.size);
  }

  // A build that failed before it was attached to its prototype has no prototype id, so it used
  // to get a second group with the prototype's name. It goes under that prototype when exactly
  // one has the name; with two of the same name there is no telling which, and it stays apart.
  for (const orphan of [...groups.values()]) {
    if (!orphan.key.startsWith(NAME_KEY)) continue;
    const owners = [...groups.values()].filter(
      (g) => !g.key.startsWith(NAME_KEY) && g.name === orphan.name
    );
    if (owners.length !== 1) continue;
    owners[0].entries.push(...orphan.entries);
    owners[0].entries.sort(
      (a, b) => (order.get(b.llmInteractionId) ?? 0) - (order.get(a.llmInteractionId) ?? 0)
    );
    groups.delete(orphan.key);
  }
  for (const prototype of shared ?? []) {
    addSharedVersions(groups, prototype, { activeTurnId, publishedVersionIds });
  }
  for (const group of groups.values()) {
    const newestUnfinished = group.entries.find((e) => e.state === 'unfinished');
    if (newestUnfinished) newestUnfinished.canContinue = true;
  }

  // The prototype on screen first, then the rest newest first.
  const list = [...groups.values()];
  const newest = (g: VersionMenuGroup): number => {
    const t = g.entries[0]?.createdAt ? new Date(g.entries[0].createdAt).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  };
  list.sort((a, b) => {
    const aCur = a.entries.some((e) => e.isCurrent) ? 1 : 0;
    const bCur = b.entries.some((e) => e.isCurrent) ? 1 : 0;
    if (aCur !== bCur) return bCur - aCur;
    return newest(b) - newest(a);
  });
  return list;
}

/**
 * The unfinished builds the menu lists, each with whether it gets a button (Continue or Build
 * again): only the newest one of its prototype does. The chat cards read this too, so a card and
 * its menu row agree; an older failed build's card offered Build again (test, 15 Oct 2026).
 */
export function unfinishedBuildButtons(input: {
  messages: readonly Message[];
  versions?: TurnVersionsLookup;
}): Map<string, boolean> {
  const buttons = new Map<string, boolean>();
  for (const group of buildPrototypeVersionMenu(input)) {
    for (const entry of group.entries) {
      if (entry.state === 'unfinished') buttons.set(entry.llmInteractionId, entry.canContinue);
    }
  }
  return buttons;
}

/** The scope the version was made in, "Clinic Operations". The person is not shown. */
export function madeInLabel(version: PrototypeVersion | undefined): string | undefined {
  const scope = (version?.madeInConversationTitle ?? '').trim();
  return scope || undefined;
}

function finishedVersion(version: PrototypeVersion): boolean {
  if (!version.llmInteractionId || versionSetAside(version.status)) return false;
  return (
    version.status === 'verified' || (typeof version.number === 'number' && version.number > 0)
  );
}

function entryTime(entry: VersionMenuEntry): number {
  const t = entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * A shared prototype's group: this conversation's own rows say where each was made, and the
 * finished versions other scopes made join them, newest first. Another scope's unfinished build
 * is not listed: only that scope can continue or remove it.
 */
function addSharedVersions(
  groups: Map<string, VersionMenuGroup>,
  prototype: SharedPrototypeHistory,
  context: { activeTurnId?: string | null; publishedVersionIds?: ReadonlySet<string> }
): void {
  const byTurn = new Map(
    prototype.versions
      .filter((v) => v.llmInteractionId)
      .map((v) => [v.llmInteractionId as string, v] as const)
  );
  const existing = groups.get(prototype.id);
  const listed = new Set(existing?.entries.map((e) => e.llmInteractionId) ?? []);
  for (const entry of existing?.entries ?? []) {
    entry.madeIn = madeInLabel(byTurn.get(entry.llmInteractionId)) ?? entry.madeIn;
  }
  const others = prototype.versions.filter(
    (v) => finishedVersion(v) && !listed.has(v.llmInteractionId as string)
  );
  // Nothing made elsewhere: this scope's rows keep their order, and no empty group appears.
  if (others.length === 0) return;
  const group: VersionMenuGroup = existing ?? {
    key: prototype.id,
    name: resolvePrototypeName({ prototypeTitle: prototype.title }) ?? 'Prototype',
    entries: [],
  };
  groups.set(prototype.id, group);
  for (const version of others) {
    const lid = version.llmInteractionId as string;
    group.entries.push({
      llmInteractionId: lid,
      label:
        typeof version.number === 'number' && version.number > 0 ? `V${version.number}` : 'Version',
      state: 'finished',
      createdAt: version.createdAt,
      isCurrent: lid === context.activeTurnId,
      isPublished: Boolean(context.publishedVersionIds?.has(version.id)),
      versionId: version.id,
      canContinue: false,
      action: unfinishedBuildAction(version.status),
      canRemove: false,
      madeIn: madeInLabel(version),
    });
  }
  group.entries.sort((a, b) => entryTime(b) - entryTime(a));
}

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function relativeTime(iso: string | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const minutes = Math.floor(Math.max(0, now - t) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}
